import type { ReactNode } from 'react'
import {
  isEmpty,
  isEmptyAll,
  SECOND,
  type WithOptionalExpiresAt,
  type AuditProps,
  getLastUpdatedBy,
  getLastUpdatedAt,
} from '@terros/common'
import { Group } from '@mantine/core'
import { RelativeInfo } from '../RelativeInfo'
import { AuditRelativeInfo } from './AuditRelativeInfo'

export const AuditInfo = ({
  createdAt,
  createdBy,
  updatedAt,
  updatedBy,
  expiresAt,
}: AuditProps & WithOptionalExpiresAt): ReactNode => {
  if (isEmptyAll([createdAt, createdBy, updatedAt, updatedBy])) return

  const expiresAtMs = expiresAt ? expiresAt * SECOND : undefined

  return (
    <Group>
      <RelativeInfo at={createdAt} by={createdBy} type='Created' />
      <RelativeInfo at={updatedAt} by={updatedBy} type='Updated' />
      <RelativeInfo at={expiresAtMs} type='Expires' />
    </Group>
  )
}

export const AuditInfoSection = ({
  createdAt,
  createdBy,
  updatedAt,
  updatedBy,
  lastAccess,
  firstAccess,
  modifiedBy,
  expiresAt: expiresAt,
}: AuditProps & { lastAccess?: number; firstAccess?: number } & WithOptionalExpiresAt): ReactNode => {
  if (isEmptyAll([createdAt, createdBy, updatedAt, updatedBy, firstAccess, lastAccess, modifiedBy])) return
  const expiresAtMs = expiresAt ? expiresAt * SECOND : undefined

  if (isEmpty(modifiedBy)) {
    return (
      <AuditInfo
        createdAt={createdAt}
        createdBy={createdBy}
        updatedAt={updatedAt}
        updatedBy={updatedBy}
        expiresAt={expiresAt}
      />
    )
  }

  const lastUpdatedBy = getLastUpdatedBy({ modifiedBy, updatedBy })
  const lastUpdatedAt = getLastUpdatedAt({ modifiedBy, updatedAt })

  return (
    <>
      <AuditRelativeInfo at={lastUpdatedAt} by={lastUpdatedBy} modifiedBy={modifiedBy} type='Last Updated' />
      <AuditRelativeInfo at={expiresAtMs} type='Expires' />
      <AuditRelativeInfo at={lastAccess} type='Last Access' />
      <AuditRelativeInfo at={firstAccess} type='First Access' />
    </>
  )
}
